import axios, { type AxiosInstance } from "axios";
import { AGENT_API_BASE_URL } from "@/shared/constants";
import { attachAuthInterceptor } from "./authInterceptor";
import { endSession, getAccessToken } from "./tokenAccessor";

export interface AiStreamConfig {
  /** Aborts the request and stops the line iterator. */
  signal?: AbortSignal;
  /** Defaults to POST — every streaming agent route takes a body. */
  method?: "POST" | "GET";
}

export interface AiStreamResponse {
  ok: boolean;
  status: number;
  /**
   * The NDJSON body, one non-blank line per yield, already decoded and
   * re-joined across chunk boundaries. Callers own JSON.parse so a malformed
   * line stays their decision.
   *
   * Single-use: it locks the underlying body, so call it once and only when
   * `ok` is true. Breaking out of the loop (or aborting via `signal`) cancels
   * the body.
   */
  lines: () => AsyncGenerator<string, void, undefined>;
}

export interface AiClient extends AxiosInstance {
  /**
   * A streaming POST, for routes that emit NDJSON as the agent works
   * (/invoke and friends).
   *
   * Streaming is why this can't be a plain axios call: axios uses XHR in the
   * browser, which exposes no readable response stream. So this is fetch
   * underneath, wrapped to keep the parts that matter identical to the rest of
   * the client — same baseURL, same bearer token, same single 401 retry, same
   * session teardown.
   *
   * Unlike the axios methods it does NOT reject on a non-2xx status; it
   * reports `ok`/`status` instead, because callers branch on specific codes
   * (a 504 from API Gateway means the agent is still running server-side and
   * the turn should be recovered, not surfaced as an error).
   */
  stream(
    url: string,
    data?: unknown,
    config?: AiStreamConfig,
  ): Promise<AiStreamResponse>;
}

/**
 * The agent/AI API (ai.{stage}.kardanalytics.com) — narrative templates,
 * chat invoke, thread history, HITL receive, media presign, Deepgram tokens.
 *
 * This is the ONE place the agent domain is configured. Callers must not
 * rebuild URLs from AGENT_API_BASE_URL themselves; the constant exists to
 * feed this client.
 *
 * The baseURL must stay absolute. A relative one only resolves in dev, where
 * the vite proxy forwards it — in deployed builds the CloudFront distribution
 * has no /ai/* behavior, so the request falls through to the SPA
 * error-response rule and returns index.html with a 200.
 */
const aiClient = axios.create({
  baseURL: AGENT_API_BASE_URL,
  headers: { "Content-Type": "application/json" },
}) as AiClient;

attachAuthInterceptor(aiClient);

/** Splits a byte stream into NDJSON lines, joining across chunk boundaries. */
async function* ndjsonLines(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<string, void, undefined> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const complete = buffer.split("\n");
      // The last element is a partial line (or "") — hold it for the next chunk.
      buffer = complete.pop() ?? "";
      for (const line of complete) {
        if (line.trim()) yield line;
      }
    }
    // Flush a final line that arrived without a trailing newline. Dropping it
    // would lose a terminal `complete` event and make a finished turn look
    // like a dropped stream.
    buffer += decoder.decode();
    if (buffer.trim()) yield buffer;
  } finally {
    // Runs on early break and on abort too, so the body is never left
    // undrained with its lock held.
    await reader.cancel().catch(() => {});
  }
}

aiClient.stream = async function stream(url, data, config) {
  const method = config?.method ?? "POST";
  // fetch throws TypeError on a GET carrying a body, so the body is dropped
  // rather than passed through — a GET caller that also supplies `data` gets a
  // bodyless request instead of a rejected promise.
  const body =
    method === "GET" || data === undefined ? undefined : JSON.stringify(data);

  const send = async (token: string): Promise<Response> =>
    fetch(aiClient.getUri({ url }), {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body,
      signal: config?.signal,
    });

  let response = await send(await getAccessToken());

  // Mirrors attachAuthInterceptor: one retry with a fresh token on 401, and
  // end the session if that still fails.
  if (response.status === 401) {
    let retryToken: string;
    try {
      retryToken = await getAccessToken();
    } catch (error) {
      endSession();
      throw error;
    }
    response = await send(retryToken);
    if (response.status === 401) endSession();
  }

  return {
    ok: response.ok,
    status: response.status,
    lines: () => {
      if (!response.body) {
        throw new Error(`${url}: response has no body to stream`);
      }
      return ndjsonLines(response.body);
    },
  };
};

export default aiClient;
